import { useEffect, useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { safeSessionStorage } from '../../lib/safeStorage';
import { DelayedPageLoader } from '../ui/PageLoader';

export function PublicOnlyRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Read redirect target once and memoize it so StrictMode double-renders
  // don't lose the value (sessionStorage reads are side-effect-free).
  const redirectTarget = useMemo(() => {
    if (!isAuthenticated) return null;

    if (location.pathname === '/provider-onboarding') {
      return '/faxes/send';
    }
    if (location.pathname === '/records-request-onboarding') {
      return '/records-requests/new';
    }

    const isPostLogout = safeSessionStorage.getItem('isPostLogout');
    if (isPostLogout) {
      return '/';
    }

    const storedRedirect = safeSessionStorage.getItem('redirectAfterLogin');
    if (storedRedirect) {
      return storedRedirect;
    }

    // Preserve the query string, not just the pathname. Cold deep links (a
    // logged-out user opening a link that carries state in the URL) reach
    // /login through ProtectedRoute's `state.from`, and dropping `search`
    // silently strips that state — e.g. the faxed join-request QR code lands
    // on /staff?inviteEmail=… and the prefill is load-bearing: the accept path
    // requires an exact email match, so a lost or retyped address is an invite
    // the requester can never accept.
    const from = (
      location.state as { from?: { pathname?: string; search?: string } }
    )?.from;
    if (from?.pathname) {
      return `${from.pathname}${from.search ?? ''}`;
    }

    return '/';
  }, [isAuthenticated, location.pathname, location.state]);

  // Clean up sessionStorage after the redirect target is determined.
  // Using useEffect ensures this runs once, even with StrictMode double-renders.
  useEffect(() => {
    if (!isAuthenticated) return;

    const isPostLogout = safeSessionStorage.getItem('isPostLogout');
    if (isPostLogout) {
      safeSessionStorage.removeItem('isPostLogout');
      safeSessionStorage.removeItem('redirectAfterLogin');
    } else {
      safeSessionStorage.removeItem('redirectAfterLogin');
    }
  }, [isAuthenticated]);

  if (isLoading) {
    return <DelayedPageLoader />;
  }

  // If user is already authenticated, redirect appropriately
  if (isAuthenticated && redirectTarget) {
    return <Navigate to={redirectTarget} replace />;
  }

  // Otherwise, render the public route (login, etc.)
  return <Outlet />;
}
